import { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  useSubscription,
  useStoredPlan,
  usePlans,
  formatPrice,
  formatStripeCents,
  type SubscriptionPlan,
  type PlanChangePreview,
} from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { ArrowRight, ArrowDown, Check, Loader2 } from 'lucide-react';

export function PlanChangePage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { plan?: string; period?: string };
  const { subscription, previewPlanChange, changePlan, isChangePlanLoading, isPreviewLoading } = useSubscription();
  const { clearStoredPlan } = useStoredPlan();
  const { plansByType, isLoading: isPlansLoading } = usePlans();
  const { user } = useAuth();

  const [preview, setPreview] = useState<PlanChangePreview | null>(null);

  const currentPlan = subscription?.current_plan;
  const newPlanType = search?.plan as SubscriptionPlan | undefined;
  const billingPeriod = (search?.period as 'monthly' | 'yearly') || 'monthly';
  const userName = user?.user_metadata?.full_name?.split(' ')[0] || '';

  // Get the new plan from the database
  const newPlan = newPlanType && plansByType?.[newPlanType]?.[billingPeriod];

  // Validate plans
  const isValidPlan = newPlanType && ['solo', 'pro', 'team'].includes(newPlanType);

  // Determine upgrade/downgrade from tier_rank (lower = better tier)
  // This works even when the preview API fails
  const isUpgrade = !!(currentPlan && newPlan && newPlan.tier_rank < currentPlan.tier_rank);
  const isDowngrade = !!(currentPlan && newPlan && newPlan.tier_rank > currentPlan.tier_rank);

  // Fetch proration preview when page loads
  useEffect(() => {
    if (newPlan?.id && subscription?.stripe_subscription_id) {
      previewPlanChange({ newPlanId: newPlan.id })
        .then((result) => {
          if (result) {
            setPreview(result);
          }
        })
        .catch(() => {
          // Preview may fail in test mode - tier_rank-based detection still works
        });
    }
  }, [newPlan?.id, subscription?.stripe_subscription_id, previewPlanChange]);

  const handleChangePlan = async () => {
    if (!newPlan?.id) return;

    try {
      const result = await changePlan({ newPlanId: newPlan.id });

      clearStoredPlan();

      // API returns camelCase: changeType, effectiveDate, newPlan
      if (isDowngrade || result?.changeType === 'downgrade') {
        const effectiveDate = result?.effectiveDate
          ? new Date(result.effectiveDate).toLocaleDateString()
          : 'the end of your billing period';
        toast({
          title: 'Downgrade scheduled',
          description: `Your plan will change to ${newPlan.plan_name} on ${effectiveDate}. You'll keep your current plan benefits until then.`,
        });
      } else {
        toast({
          title: 'Plan upgraded!',
          description: `You've been upgraded to the ${newPlan.plan_name} plan. Changes are effective immediately.`,
        });
      }

      navigate({ to: '/app/billing' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to change plan',
        variant: 'destructive',
      });
    }
  };

  const handleKeepCurrent = () => {
    clearStoredPlan();
    navigate({ to: '/app/billing' });
  };

  // Loading state
  if (isPlansLoading || !plansByType) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  // Invalid plan or no current subscription
  if (!isValidPlan || !currentPlan || !newPlan) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <h1 className="font-display text-xl font-semibold">Invalid Plan</h1>
            <p className="mt-2 text-muted-foreground">
              The requested plan is not valid.
            </p>
            <Button onClick={() => navigate({ to: '/app/dashboard' })} className="mt-4">
              Go to Dashboard
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex flex-1 items-center justify-center py-12">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <div className="text-center">
              <h1 className="font-display text-3xl font-bold">
                Welcome back{userName ? `, ${userName}` : ''}!
              </h1>
              <p className="mt-4 text-muted-foreground">
                You're currently on the <span className="font-medium">{currentPlan.plan_name}</span> plan
                ({formatPrice(currentPlan.price)}/{currentPlan.billing_period === 'yearly' ? 'year' : 'month'}).
              </p>
              <p className="mt-2 text-muted-foreground">
                You selected <span className="font-medium">{newPlan.plan_name}</span> ({formatPrice(newPlan.price)}/{billingPeriod === 'yearly' ? 'year' : 'month'})
                {' — '}would you like to {isUpgrade ? 'upgrade' : isDowngrade ? 'downgrade' : 'change'}?
              </p>
            </div>

            {/* Plan comparison */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {/* Current plan */}
              <div className="rounded-card border bg-card p-6">
                <p className="text-sm text-muted-foreground">Current Plan</p>
                <h3 className="mt-1 font-display text-xl font-semibold">
                  {currentPlan.plan_name}
                </h3>
                <p className="mt-2">
                  <span className="text-2xl font-bold">{formatPrice(currentPlan.price)}</span>
                  <span className="text-muted-foreground">/{currentPlan.billing_period === 'yearly' ? 'year' : 'month'}</span>
                </p>
                <ul className="mt-4 space-y-2">
                  {(Array.isArray(currentPlan.features?.feature_list)
                    ? currentPlan.features.feature_list as string[]
                    : []
                  ).map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-sapling-primary-100" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* New plan */}
              <div className="rounded-card border-2 border-sapling-primary-100 bg-card p-6">
                <p className="text-sm text-sapling-primary-100">New Plan</p>
                <h3 className="mt-1 font-display text-xl font-semibold">
                  {newPlan.plan_name}
                </h3>
                <p className="mt-2">
                  <span className="text-2xl font-bold">{formatPrice(newPlan.price)}</span>
                  <span className="text-muted-foreground">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                </p>
                <ul className="mt-4 space-y-2">
                  {(Array.isArray(newPlan.features?.feature_list)
                    ? newPlan.features.feature_list as string[]
                    : []
                  ).map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-sapling-primary-100" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Proration preview / Downgrade info */}
            {isPreviewLoading ? (
              <div className="mt-6 flex items-center justify-center gap-2 rounded-btn bg-muted p-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Calculating...</span>
              </div>
            ) : preview ? (
              <div className="mt-6">
                {isUpgrade && preview.proration && (
                  <div className="rounded-btn bg-sapling-primary-100/10 p-4 text-center">
                    <p className="text-sm text-sapling-primary-200">
                      Amount due today: <span className="font-semibold">{formatStripeCents(preview.proration.amount_due)}</span>
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Prorated for the remaining billing period
                    </p>
                  </div>
                )}
                {isDowngrade && preview.scheduledDate && (
                  <div className="rounded-btn bg-sapling-secondary-100/10 p-4">
                    <div className="flex items-center gap-2 text-sapling-secondary-200">
                      <ArrowDown className="h-4 w-4" />
                      <span className="text-sm font-medium">Scheduled downgrade</span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Your plan will change on {new Date(preview.scheduledDate).toLocaleDateString()}.
                      You'll keep {currentPlan.plan_name} benefits until then.
                    </p>
                  </div>
                )}
              </div>
            ) : isDowngrade ? (
              <div className="mt-6">
                <div className="rounded-btn bg-sapling-secondary-100/10 p-4">
                  <div className="flex items-center gap-2 text-sapling-secondary-200">
                    <ArrowDown className="h-4 w-4" />
                    <span className="text-sm font-medium">Scheduled downgrade</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    This change will take effect at the end of your current billing period.
                    You'll keep {currentPlan.plan_name} benefits until then.
                  </p>
                </div>
              </div>
            ) : null}

            {/* Actions */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                onClick={handleChangePlan}
                loading={isChangePlanLoading}
                disabled={isPreviewLoading}
                size="lg"
              >
                {isUpgrade ? `Upgrade to ${newPlan.plan_name}` : isDowngrade ? `Schedule Downgrade` : `Change to ${newPlan.plan_name}`}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={handleKeepCurrent}
                disabled={isChangePlanLoading}
                size="lg"
              >
                Keep {currentPlan.plan_name}
              </Button>
            </div>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {isUpgrade
                ? "Upgrades take effect immediately. You'll be charged the prorated difference."
                : isDowngrade
                  ? "Downgrades take effect at the end of your billing period."
                  : "Plan changes may involve prorated charges or credits."}
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
